import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload, Send, Shield, Clock, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RETURN_REASONS = [
  "Product damaged in transit (e.g., broken bottle, opened seal)",
  "Incorrect item or quantity received",
  "Order arrived much later than expected",
  "Quality concern / suspected defect",
  "Change of mind (Item is unopened and sealed)",
];

export default function Returns() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customerName: '', email: '', orderNumber: '', reason: '', additionalComments: '', resolutionPreference: 'replacement',
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerName || !form.email || !form.orderNumber || !form.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/returns`, {
        customer_name: form.customerName,
        email: form.email,
        order_number: form.orderNumber,
        reason: form.reason,
        additional_comments: form.additionalComments,
        resolution_preference: form.resolutionPreference,
      });
      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to submit return request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-24 md:py-32">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold mb-4 font-heading" data-testid="return-submitted-title">Return Request Submitted</h1>
            <p className="text-lg text-muted-foreground mb-8">We have received your return request for Order #{form.orderNumber}. Our support team is reviewing your submission and will reply within 1-2 business days with your next steps and return shipping instructions.</p>
            <button onClick={() => navigate('/')} className="bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-8 rounded-xl">Back to Home</button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 font-heading" data-testid="returns-title">Returns & Refunds</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">We stand by the quality of our products. If you need to return an item, please review our policy and submit a request below.</p>
        </motion.div>

        {/* Policy Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-3xl p-8 shadow-soft mb-8">
          <h2 className="text-2xl font-bold mb-6 font-heading">Return Policy</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 rounded-2xl p-5">
              <Clock className="w-6 h-6 text-blue-600 mb-3" />
              <h3 className="font-bold mb-2">14-Day Window</h3>
              <p className="text-sm text-muted-foreground">You have 14 days from delivery date to request a return or report an issue.</p>
            </div>
            <div className="bg-green-50 rounded-2xl p-5">
              <Package className="w-6 h-6 text-green-600 mb-3" />
              <h3 className="font-bold mb-2">Unopened Products</h3>
              <p className="text-sm text-muted-foreground">Change of mind returns accepted only if bottles are unopened with safety seal intact.</p>
            </div>
            <div className="bg-amber-50 rounded-2xl p-5">
              <Shield className="w-6 h-6 text-amber-600 mb-3" />
              <h3 className="font-bold mb-2">Damaged Goods</h3>
              <p className="text-sm text-muted-foreground">Broken or incorrect items qualify for free replacement or full refund.</p>
            </div>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <p className="text-sm text-red-800"><strong>Important:</strong> Do not ship products back without first obtaining a Return Merchandise Authorization (RMA) number via this form.</p>
          </div>
        </motion.div>

        {/* Return Request Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-3xl p-8 shadow-soft">
          <h2 className="text-2xl font-bold mb-6 font-heading">Submit Return Request</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Full Name *</label><input name="customerName" value={form.customerName} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" data-testid="return-name" /></div>
              <div><label className="block text-sm font-medium mb-1">Email Address *</label><input name="email" type="email" value={form.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" data-testid="return-email" /></div>
            </div>

            <div><label className="block text-sm font-medium mb-1">Order Number *</label><input name="orderNumber" value={form.orderNumber} onChange={handleChange} required placeholder="e.g. INV-1234" className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" data-testid="return-order-number" /></div>

            <div>
              <label className="block text-sm font-medium mb-1">Reason for Return *</label>
              <select name="reason" value={form.reason} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none bg-white" data-testid="return-reason">
                <option value="">Select a reason...</option>
                {RETURN_REASONS.map((reason) => (<option key={reason} value={reason}>{reason}</option>))}
              </select>
            </div>

            <div><label className="block text-sm font-medium mb-1">Additional Comments</label><textarea name="additionalComments" value={form.additionalComments} onChange={handleChange} rows={3} placeholder="Please describe the issue in detail..." className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none resize-none" data-testid="return-comments" /></div>

            <div>
              <label className="block text-sm font-medium mb-2">Resolution Preference *</label>
              <div className="flex gap-4">
                <label className={`flex-1 p-4 rounded-xl border-2 cursor-pointer text-center transition-all ${form.resolutionPreference === 'replacement' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                  <input type="radio" name="resolutionPreference" value="replacement" checked={form.resolutionPreference === 'replacement'} onChange={handleChange} className="sr-only" />
                  <Package className="w-6 h-6 mx-auto mb-2 text-primary" /><p className="font-medium text-sm">Replacement</p>
                </label>
                <label className={`flex-1 p-4 rounded-xl border-2 cursor-pointer text-center transition-all ${form.resolutionPreference === 'refund' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                  <input type="radio" name="resolutionPreference" value="refund" checked={form.resolutionPreference === 'refund'} onChange={handleChange} className="sr-only" />
                  <Shield className="w-6 h-6 mx-auto mb-2 text-primary" /><p className="font-medium text-sm">Refund</p>
                </label>
              </div>
            </div>

            <button type="submit" disabled={submitting} className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="return-submit">
              <Send className="w-5 h-5" />
              {submitting ? 'Submitting...' : 'Submit Return Request'}
            </button>
          </form>
        </motion.div>

        {/* Still need help? — capture high-intent users who can't find an answer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center bg-gradient-to-br from-primary/5 to-white rounded-3xl p-8 md:p-10"
          data-testid="returns-help-cta"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-3 font-heading">Still need help?</h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Not sure if your item qualifies, or can't find your order number? Our UK support team is happy to help — we typically reply within 1 business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="bg-primary hover:bg-primary/90 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 hover:-translate-y-1 shadow-lg"
              data-testid="returns-contact-support"
            >
              Contact Support
            </button>
            <a
              href="mailto:support@iiypv.com"
              className="inline-flex items-center justify-center border-2 border-primary text-primary font-semibold py-4 px-8 rounded-xl hover:bg-primary/5 transition-all duration-300"
              data-testid="returns-email-support"
            >
              Email support@iiypv.com
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
